import { SxProps, Theme } from "@mui/material";
import { focusRing } from "theme/colors";
import webFormsTokens, {
  PHONE_SM_QUERY,
  webFormsEmptyIcon,
  webFormsResponsiveTokens,
  WEBFORMS_PAGE_FOOTER_COLOR,
  WebFormsTemplateTone,
} from "./WebForms.tokens";

const { page, header, primaryButton, table } = webFormsTokens;
const { phone, tablet } = webFormsResponsiveTokens;

/**
 * Column track shared by the header strip and every row so the two line up.
 *
 * The phone reference has no columns at all — each form is a card whose head
 * line is `name … kebab` with the template chip beneath — so `xs` is a two
 * column / two row grid and the cells are placed explicitly (see `rowLead`,
 * `templateColumn`, `kebabColumn`). From `sm` up the grid is back, on the
 * tablet's narrower tracks; `md` restores the desktop tracks untouched.
 */
const tableGrid = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0,1fr) auto",
    sm: tablet.row.gridTemplateColumns,
    md: table.gridTemplateColumns,
  },
  columnGap: {
    xs: phone.card.headGap.std,
    sm: tablet.row.gap,
    md: table.gap,
  },
  rowGap: { xs: phone.card.gap.std, sm: 0 },
  alignItems: { xs: "flex-start", sm: "center" },
} as const;

/**
 * The narrow phone column's overrides for that grid. They live here rather
 * than inside `tableGrid` because a second `[PHONE_SM_QUERY]` key on a style
 * that spreads it would replace this block wholesale instead of merging.
 */
const tableGridNarrow = {
  columnGap: phone.card.headGap.sm,
  rowGap: phone.card.gap.sm,
} as const;

const webFormsStyles = {
  /**
   * Inner padding only. The centred measure comes from
   * `CenteredAuthenticatedLayout` wrapping the screen, exactly as Sign Requests
   * does — the two pages must sit on the same column.
   *
   * Below `md` the reference lays the page out edge to edge with its own
   * gutters (14/16px on the phone, 26px on the tablet), so the shared layout's
   * gutter and top margin are pulled back off with negative margins first and
   * the reference values applied on top. It also spaces the blocks with one
   * column gap instead of the per-block bottom margins the desktop screen
   * uses. From `md` up nothing changes.
   */
  page: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: phone.gap.std, sm: tablet.gap, md: 0 },
    mt: { xs: "-16px", sm: "-32px", md: 0 },
    mx: { xs: "-16px", sm: "-24px", md: 0 },
    pt: { xs: phone.paddingTop.std, sm: tablet.paddingTop, md: page.paddingTop },
    px: { xs: phone.paddingX.std, sm: tablet.paddingX, md: page.paddingX },
    pb: {
      xs: phone.paddingBottom.std,
      sm: tablet.paddingBottom,
      md: page.paddingBottom,
    },
    [PHONE_SM_QUERY]: {
      gap: phone.gap.sm,
      paddingTop: phone.paddingTop.sm,
      paddingLeft: phone.paddingX.sm,
      paddingRight: phone.paddingX.sm,
      paddingBottom: phone.paddingBottom.sm,
    },
  } as SxProps<Theme>,

  header: {
    display: "flex",
    // The tablet reference bottom-aligns the button with the count line; the
    // phone has no title above that line left to align against.
    alignItems: { xs: "center", sm: "flex-end" },
    justifyContent: "space-between",
    flexDirection: "row",
    gap: { xs: "12px", sm: tablet.headerGap, md: header.gap },
    mb: { xs: 0, sm: 0, md: header.marginBottom },
  } as SxProps<Theme>,

  /** Title + count column; must be shrinkable beside the phone button. */
  headerText: {
    minWidth: 0,
  } as SxProps<Theme>,

  /** The phone reference carries the screen name in the app bar, not here. */
  headerTitle: {
    display: { xs: "none", sm: "block" },
    m: 0,
    color: header.titleColor,
    fontSize: { sm: tablet.titleFontSize, md: header.titleFontSize },
    fontWeight: header.titleFontWeight,
    letterSpacing: header.titleLetterSpacing,
    lineHeight: 1.2,
  } as SxProps<Theme>,

  /**
   * Below `md` the reference replaces the strapline with the running count —
   * "<b>8</b> web forms" — so the two ship as separate lines that swap at the
   * breakpoint rather than one line that changes wording.
   */
  headerSubtitle: {
    display: { xs: "none", md: "block" },
    mt: header.subtitleMarginTop,
    color: header.subtitleColor,
    fontSize: header.subtitleFontSize,
    lineHeight: 1.4,
  } as SxProps<Theme>,

  headerCount: {
    display: { xs: "block", md: "none" },
    m: 0,
    mt: { xs: 0, sm: tablet.subtitleMarginTop },
    color: header.subtitleColor,
    fontSize: { xs: phone.subtitleFontSize.std, sm: tablet.subtitleFontSize },
    lineHeight: { xs: phone.subtitleLineHeight, sm: 1.4 },
    [PHONE_SM_QUERY]: { fontSize: phone.subtitleFontSize.sm },
  } as SxProps<Theme>,

  headerCountValue: {
    color: table.footerValueColor,
    fontWeight: 700,
  } as SxProps<Theme>,

  headerActions: {
    display: { xs: "none", sm: "flex" },
    alignItems: "center",
    gap: header.gap,
    flexShrink: 0,
  } as SxProps<Theme>,

  /** Single "Create Web Form" button — no caret half to pair with. */
  primaryButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: { sm: tablet.primaryButton.gap, md: primaryButton.gap },
    minWidth: 0,
    height: { sm: tablet.primaryButton.height, md: "auto" },
    p: { sm: tablet.primaryButton.padding, md: primaryButton.padding },
    background: primaryButton.bg,
    borderRadius: {
      sm: tablet.primaryButton.radius,
      md: primaryButton.radius,
    },
    color: primaryButton.color,
    fontSize: { sm: tablet.primaryButton.fontSize, md: primaryButton.fontSize },
    fontWeight: primaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": { background: primaryButton.hoverBg, boxShadow: "none" },
  } as SxProps<Theme>,

  /**
   * Phone "Create" — one full-width button under the header, at every phone
   * size, matching Sign Requests and Templates.
   *
   * The reference drew a square beside the count line at 390 and floated a
   * pill over the list at 375; the pill sat on top of the rows it was meant to
   * sit beside, and the square carried no label.
   */
  createMobile: {
    display: { xs: "flex", sm: "none" },
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: phone.createButton.gap,
    p: phone.createButton.padding,
    borderRadius: phone.createButton.radius,
    background: primaryButton.bg,
    color: primaryButton.color,
    fontSize: phone.createButton.fontSize,
    fontWeight: primaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": { background: primaryButton.hoverBg, boxShadow: "none" },
    "&.Mui-disabled": { background: primaryButton.bg, opacity: 0.5 },
  } as SxProps<Theme>,

  /**
   * White rounded card wrapping the header strip, the rows and the footer.
   * On a phone the reference gives every form its own card, so the wrapper
   * loses its chrome and the rows carry it instead.
   */
  tableCard: {
    bgcolor: { xs: "transparent", sm: table.bg },
    border: { xs: "none", sm: `1px solid ${tablet.card.border}`, md: `1px solid ${table.border}` },
    borderRadius: { xs: 0, sm: tablet.card.radius, md: table.radius },
    boxShadow: { xs: "none", sm: tablet.card.shadow, md: table.shadow },
    overflow: { xs: "visible", sm: "hidden" },
  } as SxProps<Theme>,

  /**
   * Holds the rows. A plain block inside the single card from `sm` up; a
   * gapped stack of cards on a phone.
   */
  rowGroup: {
    display: { xs: "flex", sm: "block" },
    flexDirection: "column",
    gap: { xs: phone.list.gap, sm: 0 },
  } as SxProps<Theme>,

  /** Grey "NAME / TEMPLATE / CREATED" strip — desktop only. */
  columnHeader: {
    ...tableGrid,
    display: { xs: "none", md: "grid" },
    px: table.gutter,
    py: table.columnHeaderPaddingY,
    bgcolor: table.columnHeaderBg,
    borderBottom: `1px solid ${table.columnHeaderBorder}`,
    fontSize: table.columnHeaderFontSize,
    fontWeight: table.columnHeaderFontWeight,
    letterSpacing: table.columnHeaderLetterSpacing,
    color: table.columnHeaderColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /**
   * Header cells for the two columns the narrow layout drops. They carry only
   * geometry — the type comes from the strip so both cells stay in step with it.
   */
  columnHeaderCell: {
    minWidth: 0,
    display: { xs: "none", md: "block" },
  } as SxProps<Theme>,

  columnHeaderCellRight: {
    minWidth: 0,
    display: { xs: "none", md: "block" },
    textAlign: "right",
  } as SxProps<Theme>,

  visuallyHidden: {
    position: "absolute",
    width: "1px",
    height: "1px",
    p: 0,
    m: "-1px",
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
    border: 0,
  } as SxProps<Theme>,

  row: {
    ...tableGrid,
    p: {
      xs: phone.card.padding.std,
      sm: `${tablet.row.paddingY} ${tablet.row.gutter}`,
      md: `${table.rowPaddingY} ${table.gutter}`,
    },
    bgcolor: { xs: table.bg, sm: "transparent" },
    border: { xs: `1px solid ${phone.card.border}`, sm: "none" },
    borderRadius: { xs: phone.card.radius, sm: 0 },
    borderBottom: {
      xs: `1px solid ${phone.card.border}`,
      sm: `1px solid ${table.rowDivider}`,
    },
    cursor: "pointer",
    "&:hover": { bgcolor: { xs: table.bg, sm: table.rowHoverBg } },
    [PHONE_SM_QUERY]: {
      ...tableGridNarrow,
      padding: phone.card.padding.sm,
    },
  } as SxProps<Theme>,

  rowLead: {
    display: "flex",
    alignItems: { xs: "flex-start", sm: "center" },
    gap: {
      xs: phone.card.headGap.std,
      sm: tablet.row.leadGap,
      md: table.leadGap,
    },
    minWidth: 0,
    // The phone card's head line: name block on the left, kebab on the right,
    // with the template chip placed on a second row beneath both.
    gridColumn: { xs: "1", sm: "auto" },
    gridRow: { xs: "1", sm: "auto" },
    [PHONE_SM_QUERY]: { gap: phone.card.headGap.sm },
  } as SxProps<Theme>,

  iconTile: (tone: WebFormsTemplateTone) =>
    ({
      width: {
        xs: phone.card.iconTileSize.std,
        sm: tablet.row.iconTileSize,
        md: table.iconTileSize,
      },
      height: {
        xs: phone.card.iconTileSize.std,
        sm: tablet.row.iconTileSize,
        md: table.iconTileSize,
      },
      borderRadius: {
        xs: phone.card.iconTileRadius,
        sm: tablet.row.iconTileRadius,
        md: table.iconTileRadius,
      },
      bgcolor: tone.bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      [PHONE_SM_QUERY]: {
        width: phone.card.iconTileSize.sm,
        height: phone.card.iconTileSize.sm,
      },
    }) as SxProps<Theme>,

  rowTitle: {
    fontSize: {
      xs: phone.card.titleFontSize.std,
      sm: tablet.row.titleFontSize,
      md: table.titleFontSize,
    },
    fontWeight: table.titleFontWeight,
    color: table.titleColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    // Rendered as a router Link (keyboard-operable open control): keep it
    // looking like the title, but give it a visible keyboard focus ring (2.4.7).
    textDecoration: "none",
    display: "block",
    "&:focus-visible": {
      outline: `2px solid ${focusRing}`,
      outlineOffset: "2px",
      borderRadius: "2px",
    },
    [PHONE_SM_QUERY]: { fontSize: phone.card.titleFontSize.sm },
  } as SxProps<Theme>,

  rowSub: {
    mt: { xs: phone.card.subMarginTop, md: table.subMarginTop },
    fontSize: {
      xs: phone.card.subFontSize.std,
      sm: tablet.row.subFontSize,
      md: table.subFontSize,
    },
    color: table.subColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE_SM_QUERY]: { fontSize: phone.card.subFontSize.sm },
  } as SxProps<Theme>,

  /**
   * The phone card has no CREATED column, so the timestamp moves under the
   * name and takes the caption's place; from `sm` up the caption is back.
   */
  rowSubLink: {
    display: { xs: "none", sm: "inline" },
  } as SxProps<Theme>,

  rowSubCreated: {
    display: { xs: "inline", sm: "none" },
  } as SxProps<Theme>,

  /**
   * TEMPLATE column. On a phone the chip drops to a second row spanning the
   * whole card and hugs the left edge, exactly as the reference draws it.
   */
  templateColumn: {
    minWidth: 0,
    // `flex` rather than `block` on the phone: a block would open an inline
    // line box above the chip and push the card 2px taller than the reference.
    display: { xs: "flex", sm: "block" },
    gridColumn: { xs: "1 / -1", sm: "auto" },
    gridRow: { xs: "2", sm: "auto" },
    justifySelf: { xs: "start", sm: "stretch" },
  } as SxProps<Theme>,

  templateChip: (tone: WebFormsTemplateTone) =>
    ({
      display: "inline-flex",
      alignItems: "center",
      gap: { xs: phone.card.chipGap, sm: table.chipGap },
      p: { xs: phone.card.chipPadding.std, sm: table.chipPadding },
      borderRadius: table.chipRadius,
      bgcolor: tone.chipBg,
      maxWidth: "100%",
      [PHONE_SM_QUERY]: { padding: phone.card.chipPadding.sm },
    }) as SxProps<Theme>,

  templateDot: (tone: WebFormsTemplateTone) =>
    ({
      width: { xs: phone.card.chipDotSize, sm: table.chipDotSize },
      height: { xs: phone.card.chipDotSize, sm: table.chipDotSize },
      borderRadius: "50%",
      bgcolor: tone.stroke,
      flexShrink: 0,
    }) as SxProps<Theme>,

  templateLabel: (tone: WebFormsTemplateTone) =>
    ({
      fontSize: {
        xs: phone.card.chipFontSize.std,
        sm: table.chipFontSize,
      },
      fontWeight: table.chipFontWeight,
      color: tone.chipColor,
      lineHeight: "normal",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      [PHONE_SM_QUERY]: { fontSize: phone.card.chipFontSize.sm },
    }) as SxProps<Theme>,

  /** CREATED column — dropped on a phone, where `rowSub` carries the date. */
  createdColumn: {
    minWidth: 0,
    display: { xs: "none", sm: "block" },
    textAlign: "right",
    fontSize: {
      sm: tablet.row.createdFontSize,
      md: table.createdFontSize,
    },
    color: { sm: tablet.row.createdColor, md: table.createdLabelColor },
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  /** The tablet prints the bare timestamp; only the desktop labels it. */
  createdPrefix: {
    display: { sm: "none", md: "inline" },
  } as SxProps<Theme>,

  createdValue: {
    color: { sm: "inherit", md: table.createdValueColor },
    fontWeight: { sm: 400, md: table.createdValueFontWeight },
  } as SxProps<Theme>,

  kebabColumn: {
    display: "flex",
    justifyContent: { xs: "flex-end", sm: "center" },
    flexShrink: 0,
    gridColumn: { xs: "2", sm: "auto" },
    gridRow: { xs: "1", sm: "auto" },
  } as SxProps<Theme>,

  kebabButton: {
    width: {
      xs: phone.card.kebabSize.std,
      sm: tablet.row.kebabSize,
      md: table.kebabSize,
    },
    height: {
      xs: phone.card.kebabSize.std,
      sm: tablet.row.kebabSize,
      md: table.kebabSize,
    },
    p: 0,
    borderRadius: {
      xs: phone.card.kebabRadius.std,
      sm: tablet.row.kebabRadius,
      md: table.kebabRadius,
    },
    color: { xs: table.kebabColor, sm: tablet.row.kebabColor, md: table.kebabColor },
    "&:hover": {
      bgcolor: table.kebabHoverBg,
      color: table.kebabHoverColor,
    },
    [PHONE_SM_QUERY]: {
      width: phone.card.kebabSize.sm,
      height: phone.card.kebabSize.sm,
      borderRadius: phone.card.kebabRadius.sm,
    },
  } as SxProps<Theme>,

  /**
   * Strip closing the card: load-more hint and the running tally. The rule
   * above it is the last row's own divider, so the footer adds no border of its
   * own — two would read as a double line.
   *
   * Below `md` the reference moves the tally out of the card and onto the page,
   * so this one is desktop-only and `pageFooter` takes over.
   */
  footer: {
    display: { xs: "none", md: "flex" },
    alignItems: "center",
    justifyContent: "center",
    gap: table.footerGap,
    px: table.gutter,
    py: table.footerPaddingY,
    fontSize: table.footerFontSize,
    color: table.footerColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /** "Showing 8 of 8" under the list — phone and tablet. */
  pageFooter: {
    display: { xs: "flex", md: "none" },
    alignItems: "center",
    justifyContent: "center",
    gap: table.footerGap,
    fontSize: { xs: phone.footer.fontSize.std, sm: tablet.footer.fontSize },
    color: WEBFORMS_PAGE_FOOTER_COLOR,
    lineHeight: "normal",
    textAlign: "center",
    [PHONE_SM_QUERY]: { fontSize: phone.footer.fontSize.sm },
  } as SxProps<Theme>,

  footerValue: {
    color: table.footerValueColor,
    fontWeight: table.footerValueFontWeight,
  } as SxProps<Theme>,

  footerSpinner: {
    color: table.footerColor,
  } as SxProps<Theme>,

  /**
   * Shown in place of rows when the account has no web forms yet. Below `md`
   * the reference draws it as its own card, headed by a washed circle glyph.
   */
  emptyBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    bgcolor: { xs: table.bg, sm: "transparent" },
    border: { xs: `1px solid ${phone.card.border}`, sm: "none" },
    borderRadius: { xs: phone.empty.radius, sm: 0 },
    p: {
      xs: phone.empty.padding.std,
      sm: `${tablet.empty.paddingY} 24px`,
      md: `${table.emptyPaddingY} ${table.gutter}`,
    },
    textAlign: "center",
    [PHONE_SM_QUERY]: { padding: phone.empty.padding.sm },
  } as SxProps<Theme>,

  /** Washed circle over the copy — the desktop card has no glyph at all. */
  emptyIcon: {
    display: { xs: "flex", md: "none" },
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: { xs: phone.empty.iconSize.std, sm: tablet.empty.iconSize },
    height: { xs: phone.empty.iconSize.std, sm: tablet.empty.iconSize },
    mb: { xs: phone.empty.iconMarginBottom.std, sm: tablet.empty.iconMarginBottom },
    borderRadius: "50%",
    bgcolor: webFormsEmptyIcon.bg,
    color: webFormsEmptyIcon.stroke,
    [PHONE_SM_QUERY]: {
      width: phone.empty.iconSize.sm,
      height: phone.empty.iconSize.sm,
      marginBottom: phone.empty.iconMarginBottom.sm,
    },
  } as SxProps<Theme>,

  emptyTitle: {
    fontSize: {
      xs: phone.empty.titleFontSize.std,
      sm: tablet.empty.titleFontSize,
      md: table.emptyTitleFontSize,
    },
    fontWeight: { xs: 800, md: table.emptyTitleFontWeight },
    color: { xs: table.titleColor, md: table.emptyTitleColor },
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.empty.titleFontSize.sm },
  } as SxProps<Theme>,

  emptySub: {
    mt: table.emptySubMarginTop,
    maxWidth: { sm: tablet.empty.subMaxWidth, md: "none" },
    fontSize: {
      xs: phone.empty.subFontSize.std,
      sm: tablet.empty.subFontSize,
      md: table.emptySubFontSize,
    },
    color: { xs: header.subtitleColor, md: table.emptySubColor },
    lineHeight: { xs: 1.55, sm: 1.6, md: "normal" },
    [PHONE_SM_QUERY]: { fontSize: phone.empty.subFontSize.sm },
  } as SxProps<Theme>,
};

export default webFormsStyles;
